import React, { useEffect, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import toastr from 'toastr';
import { Search, Settings } from 'lucide-react';
import AdvisorViewLink from '../AdvisorViewLink';
import UserAccessManagePage from './UserAccessManagePage';
import { accessTagStyle, accountLineStyle, UserAccessProfile, viewTarget } from './userAccess';
import '../../styles/CustomComponents.css';
import '../../styles/Employee.css';

const MIN_QUERY_LENGTH = 2;

const centeredMessageStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120, textAlign: 'center',
};

const UserSearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserAccessProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchedFor, setSearchedFor] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setTruncated(false);
      setSearchedFor('');
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get('/api/Advisors/usersearch', { params: { q: term } });
        if (!cancelled) {
          setResults(res.data?.users || []);
          setTruncated(!!res.data?.truncated);
          setSearchedFor(term);
        }
      } catch {
        if (!cancelled) toastr.error('Failed to search users.');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, reloadKey]);

  if (selectedUserId) {
    return (
      <UserAccessManagePage
        userId={selectedUserId}
        onBack={() => {
          setSelectedUserId(null);
          setReloadKey(k => k + 1);
        }}
      />
    );
  }

  let content = null;
  if (searching && results.length === 0) {
    content = <div style={centeredMessageStyle}><Spinner animation="border" variant="primary" /></div>;
  } else if (searchedFor && results.length === 0) {
    content = <div style={centeredMessageStyle}><span style={{ fontWeight: 'bold' }}>No users match your search.</span></div>;
  } else if (results.length > 0) {
    content = (
      <Table hover className="ktm-table pa-table" style={{ backgroundColor: 'white', width: '100%', marginBottom: 0 }}>
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th className="pa-col-email">Email</th>
            <th>User Type</th>
            <th>Account Access To</th>
            <th>Access Type</th>
            <th>Enter Portal</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {results.map(u => (
            <tr key={u.userId}>
              <td>{u.firstName}</td>
              <td>{u.lastName}</td>
              <td className="pa-col-email"><span className="pa-email" title={u.email}>{u.email}</span></td>
              <td>{u.userType}</td>
              <td>
                {u.accounts.map((a, i) => <div key={i} style={accountLineStyle}>{a.name}</div>)}
              </td>
              <td>
                {u.accounts.map((a, i) => (
                  <div key={i} style={accountLineStyle}><span style={accessTagStyle}>{a.accessTypeLabel}</span></div>
                ))}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {u.accounts.map((a, i) => {
                  const target = viewTarget(a);
                  return (
                    <div key={i} style={accountLineStyle}>
                      {target && (
                        <AdvisorViewLink target={target} title={`View ${a.name} as ${u.email}`}>
                          View as User
                        </AdvisorViewLink>
                      )}
                    </div>
                  );
                })}
              </td>
              <td>
                <button
                  className="RT-Button RT-Button--primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', whiteSpace: 'nowrap', width: 'auto' }}
                  onClick={() => setSelectedUserId(u.userId)}
                >
                  <Settings size={14} style={{ display: 'inline-block', flexShrink: 0 }} />
                  <span>Manage Access</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, maxWidth: 360 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9c9a92' }} />
          <input
            className="form-control"
            style={{ paddingLeft: 30 }}
            placeholder="Search by name or email..."
            value={query}
            autoFocus
            onChange={e => setQuery(e.target.value)}
          />
        </div>
      </div>
      <div className="text-muted" style={{ fontSize: 12, marginBottom: 14 }}>
        Find anyone with firm or advisor portal access, whether or not they are an advisor.
      </div>
      {truncated && results.length > 0 && (
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Showing the first {results.length} matches. Refine your search to see more.
        </div>
      )}
      {content}
    </div>
  );
};

export default UserSearchPage;
